import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  BookOpen, Play, Edit2, RefreshCw, Trash2, 
  ChevronRight, Clock, Film, Users, Settings,
  AlertCircle, Check, X, Loader2
} from "lucide-react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import { useAuthStore } from "../lib/store";

const BASE_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function StoryLibraryPanel({ onEditStory, onSelectStory }) {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStory, setSelectedStory] = useState(null);
  const [regenerating, setRegenerating] = useState(null);
  const token = useAuthStore((state) => state.token);

  useEffect(() => {
    fetchStories();
  }, []);

  const fetchStories = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BASE_URL}/api/stories?limit=20`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.ok) {
        const data = await response.json();
        setStories(data.stories || []);
      }
    } catch (error) {
      console.error("Error fetching stories:", error);
      toast.error("Failed to load stories");
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerateStory = async (storyId) => {
    try {
      setRegenerating(storyId);
      const response = await fetch(`${BASE_URL}/api/stories/${storyId}/regenerate`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success("Story regeneration started!");
        fetchStories(); // Refresh list
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to regenerate story");
      }
    } catch (error) {
      console.error("Error regenerating story:", error);
      toast.error("Failed to regenerate story");
    } finally {
      setRegenerating(null);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
      case "completed":
        return "bg-[#10B981]/20 text-[#10B981] border-[#10B981]/30";
      case "generating":
      case "regenerating":
        return "bg-[#F59E0B]/20 text-[#F59E0B] border-[#F59E0B]/30";
      case "failed":
        return "bg-red-500/20 text-red-400 border-red-500/30";
      default:
        return "bg-secondary/50 text-muted-foreground border-border/50";
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "Unknown";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { 
      month: "short", 
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-[#A855F7]" />
        <span className="ml-2 text-muted-foreground">Loading stories...</span>
      </div>
    );
  }

  if (stories.length === 0) {
    return (
      <div className="text-center py-12">
        <BookOpen className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
        <h3 className="text-lg font-medium text-white mb-2">No Stories Yet</h3>
        <p className="text-muted-foreground text-sm">
          Create your first story video to see it here
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-[#A855F7]" />
          Previous Stories
        </h3>
        <Button
          variant="ghost"
          size="sm"
          onClick={fetchStories}
          className="text-muted-foreground hover:text-white"
        >
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      <div className="space-y-3">
        {stories.map((story) => (
          <motion.div
            key={story.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-4 rounded-lg bg-secondary/30 border border-border/50 hover:border-[#A855F7]/30 transition-all cursor-pointer"
            onClick={() => setSelectedStory(selectedStory?.id === story.id ? null : story)}
            data-testid={`story-item-${story.id}`}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="font-medium text-white truncate">
                    {story.title || "Untitled Story"}
                  </h4>
                  <Badge className={`text-xs ${getStatusColor(story.status)}`}>
                    {story.status}
                  </Badge>
                </div>
                
                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Film className="w-3 h-3" />
                    {story.story_config?.totalScenes || story.story_config?.scenes?.length || 0} scenes
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {story.duration || 0}s
                  </span>
                  <span className="flex items-center gap-1">
                    <Users className="w-3 h-3" />
                    {story.story_config?.characters?.length || 0} characters
                  </span>
                </div>

                {story.status === "generating" || story.status === "regenerating" ? (
                  <div className="mt-2">
                    <div className="flex items-center gap-2 text-xs text-[#F59E0B]">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      {story.progress || "Processing..."}
                    </div>
                    <div className="mt-1 h-1 bg-secondary rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-[#F59E0B] transition-all duration-300"
                        style={{ width: `${story.progress_percent || 0}%` }}
                      />
                    </div>
                  </div>
                ) : null}
              </div>

              <ChevronRight 
                className={`w-5 h-5 text-muted-foreground transition-transform ${
                  selectedStory?.id === story.id ? "rotate-90" : ""
                }`} 
              />
            </div>

            {/* Expanded Details */}
            <AnimatePresence>
              {selectedStory?.id === story.id && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mt-4 pt-4 border-t border-border/30"
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Scene List */}
                  {story.story_config?.scenes && (
                    <div className="mb-4">
                      <p className="text-xs text-muted-foreground mb-2">Scenes:</p>
                      <div className="space-y-1">
                        {story.story_config.scenes.slice(0, 5).map((scene, idx) => (
                          <div 
                            key={idx}
                            className="flex items-center gap-2 text-sm text-white/80 bg-secondary/20 px-2 py-1 rounded"
                          >
                            <span className="text-[#A855F7] font-mono text-xs">{idx + 1}</span>
                            <span className="truncate flex-1">{scene.title || `Scene ${idx + 1}`}</span>
                            <span className="text-xs text-muted-foreground">{scene.duration}s</span>
                          </div>
                        ))}
                        {story.story_config.scenes.length > 5 && (
                          <p className="text-xs text-muted-foreground pl-6">
                            +{story.story_config.scenes.length - 5} more scenes
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex items-center gap-2">
                    {(story.status === "pending" || story.status === "completed") && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 border-[#A855F7]/30 text-[#A855F7] hover:bg-[#A855F7]/10"
                          onClick={() => onEditStory && onEditStory(story)}
                          data-testid={`edit-story-${story.id}`}
                        >
                          <Edit2 className="w-4 h-4 mr-1" />
                          Edit Scenes
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 border-[#10B981]/30 text-[#10B981] hover:bg-[#10B981]/10"
                          onClick={() => handleRegenerateStory(story.id)}
                          disabled={regenerating === story.id}
                          data-testid={`regenerate-story-${story.id}`}
                        >
                          {regenerating === story.id ? (
                            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                          ) : (
                            <RefreshCw className="w-4 h-4 mr-1" />
                          )}
                          Regenerate
                        </Button>
                      </>
                    )}
                    
                    {story.status === "failed" && (
                      <Button
                        size="sm"
                        className="flex-1 bg-[#10B981] hover:bg-[#10B981]/80"
                        onClick={() => handleRegenerateStory(story.id)}
                        disabled={regenerating === story.id}
                      >
                        {regenerating === story.id ? (
                          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        ) : (
                          <RefreshCw className="w-4 h-4 mr-1" />
                        )}
                        Retry Generation
                      </Button>
                    )}
                  </div>

                  <p className="text-xs text-muted-foreground mt-3">
                    Created: {formatDate(story.created_at)}
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
